import { consoleRoutes, PLAN_FEATURES, PLAN_FEATURE_MIN_PLAN, PLAN_KEYS, type PlanKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Card, EmptyState, ErrorState, Icon, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import { RequirePermission } from "../../../../../pages/forbidden-page";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../../../session/workspace-context";

/** Whether a plan includes Connectors (the API answers 402 `feature_not_in_plan` otherwise; this only saves the round trip). */
export function planHasConnectors(plan: string | undefined): boolean {
  const key = PLAN_KEYS.find((candidate): candidate is PlanKey => candidate === plan);
  if (!key) return true;
  return (PLAN_FEATURES[key] as readonly string[]).includes("connectors");
}

export function BackLink({ href, label }: { href: string; label: string }) {
  return (
    <Button asChild variant="ghost" size="sm" className="-ml-2">
      <AppLink href={href}>
        <Icon name="arrow-left" />
        {label}
      </AppLink>
    </Button>
  );
}

export function PageHead({ title, subtitle, note, actions, titleId }: { title: ReactNode; subtitle?: ReactNode; note?: ReactNode; actions?: ReactNode; titleId?: string }) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 id={titleId} className="text-2xl font-extrabold tracking-tight break-words">
          {title}
        </h1>
        {subtitle ? <p className="mt-1 max-w-3xl text-sm text-fg-muted">{subtitle}</p> : null}
        {note}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/** Plan-locked page: the lock, what the plan adds, one way up (same pattern as the other plan gates). */
export function ConnectorsUpgradeCard() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const plan = t(`console.plans.${PLAN_FEATURE_MIN_PLAN.connectors}`);
  return (
    <Card className="flex flex-col gap-4 p-5 md:flex-row md:items-center" aria-labelledby="connectors-upgrade-title" data-testid="plan-gate">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
        <Icon name="lock" className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 id="connectors-upgrade-title" className="text-base font-bold">
          {t("service.connectors.upgrade.title", { plan })}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">{t("service.connectors.upgrade.body")}</p>
      </div>
      <Button asChild>
        <AppLink href={consoleRoutes.billing(summary.slug)}>
          <Icon name="arrow-up-right" />
          {t("service.connectors.upgrade.cta")}
        </AppLink>
      </Button>
    </Card>
  );
}

/** The same lock for one feature the plan adds on top (inbound hooks, sync jobs: Pro). */
export function FeatureUpgradeCard({ title, body }: { title: string; body: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <Card className="flex flex-col gap-4 p-5 md:flex-row md:items-center" data-testid="plan-gate">
      <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary-ink">
        <Icon name="lock" className="size-6" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-bold">{title}</h2>
        <p className="mt-1 text-sm text-fg-muted">{body}</p>
      </div>
      <Button asChild>
        <AppLink href={consoleRoutes.billing(summary.slug)}>
          <Icon name="arrow-up-right" />
          {t("service.connectors.upgrade.cta")}
        </AppLink>
      </Button>
    </Card>
  );
}

export function PageSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="mx-auto max-w-7xl space-y-6" aria-hidden="true" data-testid="connectors-loading">
      <div className="space-y-2">
        <Skeleton className="h-8 w-[220px]" />
        <Skeleton className="h-4 w-[420px] max-w-full" />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton key={index} className="h-44" />
        ))}
      </div>
    </div>
  );
}

export function LoadError({ onRetry, title }: { onRetry: () => void; title?: string }) {
  const { t } = useTranslation();
  return (
    <Card data-testid="connectors-error">
      <ErrorState title={title ?? t("service.connectors.error.title")} description={t("service.connectors.error.body")} onRetry={onRetry} retryLabel={t("service.error.retry")} />
    </Card>
  );
}

/** The Service Desk module is not switched on for this workspace (the API answers 404 before it checks the permission). */
export function NotEnabled() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <Card data-testid="connectors-off">
      <EmptyState
        icon="lock"
        title={t("service.off.title")}
        description={t("service.off.body")}
        action={
          <Button asChild variant="secondary">
            <AppLink href={consoleRoutes.dashboard(summary.slug)}>
              <Icon name="arrow-left" />
              {t("service.back")}
            </AppLink>
          </Button>
        }
      />
    </Card>
  );
}

export function NotFoundCard({ title, body, backHref, backLabel }: { title: string; body: string; backHref: string; backLabel: string }) {
  return (
    <Card data-testid="connectors-missing">
      <EmptyState
        icon="plug"
        title={title}
        description={body}
        action={
          <Button asChild variant="secondary">
            <AppLink href={backHref}>
              <Icon name="arrow-left" />
              {backLabel}
            </AppLink>
          </Button>
        }
      />
    </Card>
  );
}

function PlanGate({ children }: { children: ReactNode }) {
  const details = useWorkspaceDetails();
  if (details.isPending) return <PageSkeleton />;
  if (details.data && !planHasConnectors(details.data.plan)) {
    return (
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeadPlain />
        <ConnectorsUpgradeCard />
      </div>
    );
  }
  return children;
}

function PageHeadPlain() {
  const { t } = useTranslation();
  return <PageHead title={t("service.connectors.title")} subtitle={t("service.connectors.subtitle")} />;
}

/** Every Connectors page sits inside this guard: the member's permission (403 page), then the plan (upgrade card), then the page. */
export function ConnectorsGuard({ children }: { children: ReactNode }) {
  return (
    <RequirePermission permission="connectors.view" mode="workspace">
      <PlanGate>{children}</PlanGate>
    </RequirePermission>
  );
}
